import {
  ArticleError,
  ArticleGenerating,
} from '@/components/articles/ArticleStatus'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { AlertTriangle, DownloadIcon, Image, Send } from 'lucide-react'
import { useRef, useState } from 'react'

import { SimpleEditor } from '@/components/simple/simple-editor'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { orpc } from '@/lib/orpc/client'
import { queryClient } from '@/lib/query'
import { FloppyDiskIcon } from '@phosphor-icons/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { Editor } from '@tiptap/react'
import { toast } from 'sonner'

type Article = Awaited<ReturnType<typeof orpc.articles.getArticleInfoById.call>>

export const Route = createFileRoute('/_dashboard/edit/$articleId')({
  component: RouteComponent,
  beforeLoad: async ({ params, context }) => {
    await context.queryClient.ensureQueryData(
      orpc.articles.getArticleInfoById.queryOptions({
        input: { articleId: params.articleId },
      }),
    )
    // const article = await orpc.articles.getArticleInfoById.call({
    //   articleId: params.articleId,
    // })
    // if (!article) {
    //   throw new Error('Article not found')
    // }
    // return { article }
  },
})

function RouteComponent() {
  const router = useRouter()
  const { articleId } = Route.useParams()
  const { data: article } = useQuery(
    orpc.articles.getArticleInfoById.queryOptions({
      input: { articleId: articleId },
    }),
  )
  const [title, setTitle] = useState(article?.title ?? '')
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const publishArticleMutation = useMutation(
    orpc.articles.publishArticleManually.mutationOptions({
      onMutate: () => {
        queryClient.setQueryData(
          orpc.articles.getArticleInfoById.queryKey({
            input: { articleId: articleId },
          }),
          (old) => {
            if (!old) return undefined
            return {
              ...old,
              status: 'published' as const,
            }
          },
        )
      },
      onSuccess: () => {
        toast.success("We're publishing your article to your integrations now!")
      },
    }),
  )
  const editorRef = useRef<Editor | null>(null)
  const articleContent = useRef<string>(article?.content ?? '')
  const updateArticleMutation = useMutation(
    orpc.articles.updateArticleContent.mutationOptions({
      onSuccess: () => {
        toast.success('Article saved')
        queryClient.setQueryData(
          orpc.articles.getArticleInfoById.queryKey({
            input: { articleId: article?.id ?? '' },
          }),
          (data) => {
            if (data) {
              return {
                ...data,
                content: articleContent.current,
              }
            }
            return data
          },
        )
      },
    }),
  )
  // const articleContentIsDirty = articleContent.current !== article.content

  if (!article) {
    return <div>Article not found</div>
  }

  const handleUpdateArticle = () => {
    updateArticleMutation.mutate({
      articleId: article.id,
      content: articleContent.current,
    })
  }

  const handlePublishClick = () => {
    handleUpdateArticle()
    if (article.status === 'published') {
      // Show confirmation dialog for re-publishing
      setShowConfirmDialog(true)
    } else {
      // Direct publish for draft articles
      handlePublish()
    }
  }

  const handlePublish = async () => {
    await publishArticleMutation.mutateAsync({
      articleId: article.id,
    })
    setShowConfirmDialog(false)
  }

  if (article.status === 'writing') {
    return (
      <ArticleGenerating
        articleId={article.id}
        onComplete={() => {
          router.invalidate()
        }}
      />
    )
  }

  if (article.status !== 'published' && article.status !== 'draft') {
    return <ArticleError status={article.status} id={article.id} />
  }

  return (
    <div>
      <div className="max-w-[1920px] mx-auto p-6 relative">
        <Alert variant="warning" className="mb-6">
          <AlertTitle>Heads up!</AlertTitle>
          <AlertDescription>
            We're still working on the editor, so please expect some bugs!
          </AlertDescription>
        </Alert>
        {/* Action Buttons */}
        <div className="flex items-center justify-end mb-6 flex-col md:flex-row gap-2">
          {/* Title Input */}
          <div className="flex-1">
            <input
              placeholder="Article title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="text-2xl font-bold px-3 py-1 bg-none rounded-lg border border-border focus:outline-none w-full shadow-none focus-visible:ring-0"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-3 order-2 lg:order-first">
            {/* Content Editor */}
            <div className="min-h-[500px]">
              <SimpleEditor
                editorRef={editorRef}
                onChange={(content) => {
                  articleContent.current = content
                }}
                content={article.content ?? ''}
              />
            </div>
          </div>

          {/* Sidebar */}
          <Sidebar
            article={article}
            onSave={handleUpdateArticle}
            onPublish={handlePublishClick}
            isPublishing={publishArticleMutation.isPending}
            metaDescription={article.meta_description ?? ''}
            editorRef={editorRef}
          />
        </div>
      </div>

      {/* Confirmation Dialog for Re-publishing */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Re-publish
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to re-publish this article? This will send
              the article to all your configured integrations again and might
              create duplicate articles for your site.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={publishArticleMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              disabled={publishArticleMutation.isPending}
            >
              {publishArticleMutation.isPending
                ? 'Publishing...'
                : 'Re-publish Article'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Add this interface before the RouteComponent
interface SidebarProps {
  article: Article
  onSave: () => void
  onPublish: () => void
  isPublishing: boolean
  metaDescription?: string
  editorRef: React.RefObject<Editor | null>
}

function ExportAsButton({
  editorRef,
  articleTitle,
}: {
  editorRef: React.RefObject<Editor | null>
  articleTitle: string
}) {
  const handleExportHTML = () => {
    if (!editorRef.current) {
      toast.error('Editor not ready')
      return
    }

    const html = editorRef.current.getHTML()
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${articleTitle || 'article'}.html`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    toast.success('Article exported as HTML')
  }

  const handleExportMarkdown = async () => {
    if (!editorRef.current) {
      toast.error('Editor not ready')
      return
    }

    const html = editorRef.current.getHTML()

    const TurndownService = (await import('turndown')).default
    // @ts-ignore - no type definitions available
    const turndownPlugins = await import('turndown-plugin-gfm')
    const turndownService = new TurndownService()
    turndownService.use(turndownPlugins.gfm)
    const markdown = turndownService.turndown(html)
    const blob = new Blob([markdown], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${articleTitle || 'article'}.md`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    toast.success('Article exported as Markdown')
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="flex items-center gap-2 w-full"
          variant={'secondary'}
        >
          <DownloadIcon className="w-4 h-4" />
          Export As...
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={handleExportHTML}>HTML</DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportMarkdown}>
          Markdown
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Add the Sidebar component
function Sidebar({
  article,
  onSave,
  onPublish,
  isPublishing,
  editorRef,
}: SidebarProps) {
  return (
    <div className="sticky top-18 h-fit">
      <div className="space-y-4 lg:max-h-[calc(100vh-5rem)] max-h-none overflow-y-auto">
        <div className="flex flex-col items-center gap-2">
          <Button className="flex items-center gap-2 w-full" onClick={onSave}>
            <FloppyDiskIcon className="w-4 h-4" />
            Save Changes
          </Button>
          <Button
            className="flex items-center gap-2 w-full"
            onClick={onPublish}
            variant="secondary"
            disabled={isPublishing}
          >
            <Send className="w-4 h-4" />
            {isPublishing
              ? 'Publishing...'
              : article?.status === 'published'
                ? 'Re-publish'
                : 'Publish'}
          </Button>
          <ExportAsButton
            editorRef={editorRef}
            articleTitle={article?.title ?? 'article'}
          />
        </div>

        {/* Article Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Article Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Cover Photo
                </label>

                {!article?.coverPhotoUrl ? (
                  <div className="mt-1 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
                    <Image className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      Click to upload image
                    </p>
                  </div>
                ) : (
                  <img
                    src={article.coverPhotoUrl}
                    className="mt-1 rounded-lg w-36 h-36"
                  />
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Meta Description
                </label>
                <Textarea
                  placeholder="Brief description for search engines"
                  className="mt-1 min-h-[80px]"
                  defaultValue={article?.meta_description ?? ''}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
